import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Alert, Button, GlassTable, StatusPill } from '@jumpstone/ui-react';
import { FieldRow, NumericFieldRow } from '../../components/common/field_row';
import {
  line_net,
  pay_run_view_mode,
  total_line_items,
} from './pay_run_calculations';
import {
  DisbursementSection,
  FilingSection,
  PayslipSection,
  StatutorySection,
} from './post_run_sections';
import { PayRunLineEditDrawer } from './pay_run_line_edit_drawer';
import { PayRunContractorBlockedBanner } from './pay_run_contractor_blocked_banner';
import {
  format_basis_points,
  format_date,
  format_day,
  format_month_year,
  format_money,
  format_number,
} from '../../lib/format';
import type { ApprovalState, PayRun, PayRunLineItem } from '../../data/types';
import type { PayRunPostActionsController } from '../../data/wagepro_store';
import type {
  PayRunMutationFailure,
  PayRunMutationResult,
  SetPayRunLineDeltasInput,
} from '../../data/use_pay_runs';
import { restartSignIn } from '../../lib/auth/step_up.js';
import { PAY_RUN_STATUS_KEY } from '../../lib/label_keys';
import {
  party_label,
  resolve_approver_name,
} from './pay_run_complete_view_logic';
import type { PartyLabel } from './pay_run_complete_view_logic';
import { PayRunApprovedLine } from './pay_run_approved_line';

const EXCEPTION_KEY = {
  bank_details_incomplete: 'pay_run.exception.bank_details_incomplete',
  new_joiner_unconfigured: 'pay_run.exception.new_joiner_unconfigured',
  time_logged_on_unpaid_leave: 'pay_run.exception.time_logged_on_unpaid_leave',
} as const;

const APPROVAL_STATE_PILL = {
  not_submitted: 'pending',
  submitted: 'sent',
  approved: 'approved',
} as const;

const APPROVAL_STATE_KEY: Record<ApprovalState, string> = {
  not_submitted: 'pay_run.approval.awaiting',
  submitted: 'pay_run.approval.submitted',
  approved: 'pay_run.approval.approved',
};

export function PeriodScopeStep({ run }: { run: PayRun }): React.JSX.Element {
  const { t, locale } = useTranslation('wp');

  return (
    <>
      <FieldRow label={t('pay_run.pay_period')}>
        {t('pay_run.period_range', {
          from: format_day(run.period_start, locale),
          to: format_day(run.period_end, locale),
          period: format_month_year(run.period, locale),
        })}
      </FieldRow>
      <FieldRow label={t('pay_run.employee_scope')}>
        {t('pay_run.all_active_employees', {
          count: run.employee_count,
        })}
      </FieldRow>
      <FieldRow label={t('pay_run.pay_date')}>
        {format_date(run.pay_date, locale)}
      </FieldRow>
    </>
  );
}

export function ReviewPayStep({
  run,
  on_edit_line,
}: {
  run: PayRun;
  /**
   * Fires the real per-line adjustment/lump-sum mutation(s) for one line
   * (#23309). `undefined` when editing isn't reachable — the run isn't a
   * `draft` (backend rejects the mutation server-side anyway; hide the UI
   * for consistency), OR the OAuth grant isn't write-capable (the caller
   * decides via `usePayRuns().set_line_deltas`). Hidden entirely rather
   * than disabled, matching `ExceptionsStep`'s "hide, don't lie"
   * treatment for the acknowledge button.
   */
  on_edit_line?:
    | ((input: SetPayRunLineDeltasInput) => Promise<PayRunMutationResult>)
    | undefined;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const totals = total_line_items(run.line_items);
  const [editing_line, set_editing_line] = useState<PayRunLineItem | null>(
    null,
  );

  // Editing only makes sense on a draft run: `setPayRunLineAdjustment` /
  // `setPayRunLineLumpSum` both reject anything else with BAD_REQUEST
  // server-side (`Pay run is ${status}; …can only be set while the pay run
  // is in draft`). Hiding the affordance client-side matches that gate.
  const editable = run.status === 'draft' && on_edit_line !== undefined;

  return (
    <>
      <GlassTable>
        <table className="nv-table">
          <thead>
            <tr>
              <th scope="col">{t('pay_run.column_employee')}</th>
              <th scope="col" className="text-end">
                {t('pay_run.column_gross')}
              </th>
              <th scope="col" className="text-end">
                {t('pay_run.column_allowances')}
              </th>
              <th scope="col" className="text-end">
                {t('pay_run.column_deductions')}
              </th>
              <th scope="col" className="text-end">
                {t('pay_run.column_net')}
              </th>
              {editable && (
                <th scope="col" className="text-end">
                  <span className="sr-only">
                    {t('pay_run.line.edit_column')}
                  </span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {run.line_items.map((line) => (
              <tr key={line.employee_id}>
                <td className="font-semibold">
                  {line.employee_name}
                  {/* V387 hints: back pay is already INSIDE gross — shown as
                      a breakdown line, never added on top. */}
                  {line.back_pay_cents !== undefined &&
                    line.back_pay_cents !== 0 && (
                      <div className="text-xs font-normal text-[color:var(--ink-muted)]">
                        {t('pay_run.back_pay')}{' '}
                        <span className="num">
                          {format_money(
                            line.back_pay_cents,
                            run.currency,
                            locale,
                          )}
                        </span>
                      </div>
                    )}
                  {line.salary_changed_in_period === true && (
                    <div className="text-xs font-normal text-[color:var(--ink-muted)]">
                      {t('pay_run.salary_changed_in_period')}
                    </div>
                  )}
                </td>
                <td className="text-end num">
                  {format_money(line.gross, run.currency, locale)}
                </td>
                <td className="text-end num">
                  {format_money(line.allowances, run.currency, locale)}
                </td>
                <td className="text-end num">
                  {format_money(line.deductions, run.currency, locale)}
                </td>
                <td className="text-end num font-semibold">
                  {format_money(line_net(line), run.currency, locale)}
                </td>
                {editable && (
                  <td className="text-end">
                    <Button
                      variant="secondary"
                      size="xs"
                      onClick={() => {
                        set_editing_line(line);
                      }}
                      aria-label={t('pay_run.line.edit_row_aria', {
                        name: line.employee_name,
                      })}
                    >
                      {t('pay_run.line.edit_cta')}
                    </Button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="font-semibold">{t('pay_run.shown_subtotal')}</td>
              <td className="text-end num">
                {format_money(totals.gross, run.currency, locale)}
              </td>
              <td className="text-end num">
                {format_money(totals.allowances, run.currency, locale)}
              </td>
              <td className="text-end num">
                {format_money(totals.deductions, run.currency, locale)}
              </td>
              <td className="text-end num font-semibold">
                {format_money(totals.net, run.currency, locale)}
              </td>
              {editable && <td aria-hidden="true" />}
            </tr>
          </tfoot>
        </table>
      </GlassTable>
      {editable && (
        <PayRunLineEditDrawer
          open={editing_line !== null}
          line={editing_line}
          pay_run_id={run.id}
          onClose={() => {
            set_editing_line(null);
          }}
          onSave={on_edit_line}
        />
      )}
    </>
  );
}

export function StatutoryStep({ run }: { run: PayRun }): React.JSX.Element {
  const { t, locale } = useTranslation('wp');
  const { statutory } = run;
  const blocked_contractor_count = run.blocked_contractor_count ?? 0;

  if (blocked_contractor_count > 0) {
    // Placeholder zeros for a blocked draft — never shown as real
    // contributions (WagePro worker types).
    return <PayRunContractorBlockedBanner count={blocked_contractor_count} />;
  }

  return (
    <>
      <NumericFieldRow
        label={t('pay_run.epf_employee', {
          rate: format_basis_points(statutory.epf_employee_rate, locale),
        })}
      >
        {format_money(statutory.epf_employee, run.currency, locale)}
      </NumericFieldRow>
      <NumericFieldRow
        label={t('pay_run.epf_employer', {
          rate: format_basis_points(statutory.epf_employer_rate, locale),
        })}
      >
        {format_money(statutory.epf_employer, run.currency, locale)}
      </NumericFieldRow>
      <NumericFieldRow
        label={t('pay_run.etf_employer', {
          rate: format_basis_points(statutory.etf_employer_rate, locale),
        })}
      >
        {format_money(statutory.etf_employer, run.currency, locale)}
      </NumericFieldRow>
      <NumericFieldRow label={t('pay_run.apit_withheld')}>
        {format_money(statutory.apit_withheld, run.currency, locale)}
      </NumericFieldRow>
    </>
  );
}

export function ExceptionsStep({
  run,
  on_acknowledge,
  ack_available,
}: {
  run: PayRun;
  on_acknowledge: (exception_id: string) => void;
  /**
   * `false` when acknowledging an exception on THIS run wouldn't actually do
   * anything -- see `use_pay_runs.ts`'s `exception_ack_available` doc
   * comment (C12 follow-up, adversarial review of PR #22453 finding F4,
   * #22450: `acknowledge_exception` writes into a mock list a real pay run's
   * id never matches). The "acknowledge" button is hidden entirely rather
   * than left to silently no-op -- same "hide, don't lie" treatment
   * `is_read_only_grant` already gets elsewhere in this wizard.
   */
  ack_available: boolean;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const open = run.exceptions.filter((exception) => !exception.acknowledged);

  return (
    <>
      {open.length > 0 ? (
        <Alert
          variant="warning"
          title={t('pay_run.exceptions_blocking', { count: open.length })}
        >
          {t('pay_run.exceptions_blocking_help')}
        </Alert>
      ) : (
        <Alert variant="success" title={t('pay_run.exceptions_clear')}>
          {t('pay_run.exceptions_clear_help')}
        </Alert>
      )}
      {!ack_available && open.length > 0 && (
        <p className="nv-help mt-2" role="alert">
          {t('pay_run.exception_ack_unavailable')}
        </p>
      )}
      <div className="mt-2.5">
        {run.exceptions.map((exception) => (
          <div key={exception.id} className="wp-listrow">
            <span>
              {t(EXCEPTION_KEY[exception.kind], {
                name: exception.employee_name ?? '',
              })}
            </span>
            {exception.acknowledged ? (
              <StatusPill status="approved" label={t('pay_run.acknowledged')} />
            ) : ack_available ? (
              <Button
                variant="secondary"
                size="xs"
                onClick={() => {
                  on_acknowledge(exception.id);
                }}
              >
                {t('pay_run.acknowledge')}
              </Button>
            ) : null}
          </div>
        ))}
      </div>
    </>
  );
}

export function ApprovalStep({
  run,
  can_sign,
  sign_off_available,
  is_signing_off,
  sign_off_error,
  on_sign_off,
  approved_by_name,
}: {
  run: PayRun;
  /** The wizard's own business gate (`can_sign_off`) — whether the preparer has submitted and this run isn't already approved. */
  can_sign: boolean;
  /**
   * `sign_off !== undefined` from `usePayRuns()` (C7, #22329) — this
   * component ONLY ever renders once the pay-run read state is `ready`
   * (`run_payroll_screen.tsx` gates on that before mounting `ApprovalStep`
   * at all), so in THIS component's context `false` means specifically "the
   * OAuth grant isn't write-capable" (`!isWriteCapableGrant(grant)` —
   * `grant.mode === 'read'`, OR `mode: 'write'` with `scopes` missing
   * `payroll:employer:write`, second-round adversarial review finding 3) —
   * never "the read state isn't ready" (`sign_off`'s OTHER `undefined`
   * cause, which never reaches here). The sign-off affordance is HIDDEN
   * entirely when `false` rather than shown disabled, per the acceptance
   * criteria: a token that can't actually write must never be offered a
   * button that would 403 on click. Do not reuse this prop's name/shape to
   * infer "not write-capable" in a context where the read-state-not-ready
   * cause could also apply (adversarial review finding 4) — use
   * `usePayRuns()`'s own `is_read_only_grant` there instead.
   */
  sign_off_available: boolean;
  /** True while a real sign-off mutation is in flight — disables the button and swaps its label, mirroring `settings_screen.tsx`'s `is_saving_organisation` convention. */
  is_signing_off: boolean;
  /**
   * Set only after a real sign-off mutation fails (C7, #22329) — never an
   * optimistic success; `undefined` clears any previous error on retry.
   * `'step_up_required'` (#22821) is the AAL2 step-up rejection from
   * `assertMoneyWriteAllowed` — rendered with dedicated 2FA guidance (a link
   * to security settings plus a "sign in again" action), never the generic
   * retry copy, because a blind retry can never succeed against it.
   */
  sign_off_error: PayRunMutationFailure | undefined;
  on_sign_off: () => void;
  /**
   * The approver's display name from the run's audit feed (resolved by the
   * screen, never fetched here), or `undefined` when unknown. Only used once
   * the run is already approved.
   */
  approved_by_name?: string;
}): React.JSX.Element {
  const { t } = useTranslation('wp');
  const { approval } = run;
  // Approved/paid/filed: there is nothing left to sign off, so the button is
  // replaced by who approved it and when.
  const is_already_approved = pay_run_view_mode(run) === 'complete';
  const approver_name = resolve_approver_name(approved_by_name, run);
  // Blocked either up front (the preview already reports a worker now
  // recorded as a contractor) or by approvePayRun's own refusal — both mean
  // the only way forward is deleting and recreating the draft.
  const blocked_contractor_count = run.blocked_contractor_count ?? 0;
  const is_contractor_blocked =
    blocked_contractor_count > 0 || sign_off_error === 'has_contractor';

  return (
    <>
      <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
        <ApprovalParty
          role_label={t('pay_run.preparer')}
          label={party_label(approval.preparer_name)}
          state={approval.preparer_state}
        />
        <ApprovalParty
          role_label={t('pay_run.approver')}
          label={party_label(approver_name ?? '', approval.approver_title)}
          state={approval.approver_state}
        />
      </div>
      <div className="mt-3.5">
        {is_already_approved ? (
          <PayRunApprovedLine
            approved_at={run.approved_at}
            approver_name={approver_name}
            className="nv-help mt-0"
          />
        ) : sign_off_available ? (
          <>
            <Button
              variant="primary"
              size="sm"
              disabled={!can_sign || is_signing_off || is_contractor_blocked}
              onClick={on_sign_off}
            >
              {is_signing_off
                ? t('pay_run.signing_off')
                : t('pay_run.sign_off')}
            </Button>
            {is_contractor_blocked ? (
              <div className="mt-3" role="alert">
                <PayRunContractorBlockedBanner
                  count={blocked_contractor_count}
                />
              </div>
            ) : sign_off_error === 'step_up_required' ? (
              <div className="mt-2" role="alert">
                <p className="nv-help is-error mt-0">
                  {t('pay_run.sign_off_step_up_required')}
                </p>
                <div className="flex gap-2 mt-2">
                  <Button
                    variant="secondary"
                    size="xs"
                    onClick={() => {
                      restartSignIn();
                    }}
                  >
                    {t('auth.step_up.sign_in_again_cta')}
                  </Button>
                </div>
              </div>
            ) : sign_off_error === 'entitlement_required' ? (
              <p className="nv-help is-error mt-2" role="alert">
                {t('pay_run.entitlement_required_description')}
              </p>
            ) : sign_off_error ? (
              <p className="nv-help is-error mt-2" role="alert">
                {t('pay_run.sign_off_error')}
              </p>
            ) : null}
          </>
        ) : (
          <p className="nv-help mt-0">{t('pay_run.read_only_notice')}</p>
        )}
      </div>
    </>
  );
}

function ApprovalParty({
  role_label,
  label,
  state,
}: {
  role_label: string;
  /** `undefined` when the name is unknown — the line is hidden, never a dash. */
  label: PartyLabel | undefined;
  state: ApprovalState;
}): React.JSX.Element {
  const { t } = useTranslation('wp');

  return (
    <div className="rounded-xl border border-[color:var(--line)] p-3.5">
      <p className="text-[11.5px] uppercase tracking-wider text-[color:var(--ink-muted)] font-bold mb-1.5">
        {role_label}
      </p>
      {label !== undefined && (
        <p className="font-semibold mb-2">
          {label.kind === 'name_title'
            ? t('pay_run.person_title', {
                name: label.name,
                title: label.title,
              })
            : label.name}
        </p>
      )}
      <StatusPill
        status={APPROVAL_STATE_PILL[state]}
        label={t(APPROVAL_STATE_KEY[state])}
      />
    </div>
  );
}

export function FinalReviewStep({
  run,
  finalise_available,
  is_finalising,
  finalise_error,
  on_finalise,
}: {
  run: PayRun;
  /**
   * `finalise_pay_run !== undefined` from `usePayRuns()` (#23308). `false`
   * means the OAuth grant isn't write-capable — the button is hidden
   * entirely rather than shown disabled, same "hide, don't lie" treatment
   * as `sign_off_available`. `run_payroll_screen.tsx` only mounts this step
   * once the read state is `ready`, so `false` here is grant-shaped, not
   * read-shaped.
   */
  finalise_available: boolean;
  /** True while a real finalise mutation is in flight — mirrors `is_signing_off`. */
  is_finalising: boolean;
  /**
   * Set only after a real finalise mutation fails (#23308). `'step_up_required'`
   * follows the same 2FA guidance shape as `ApprovalStep`; every other
   * failure resolves to the generic retry copy.
   */
  finalise_error: PayRunMutationFailure | undefined;
  on_finalise: () => void;
}): React.JSX.Element {
  const { t, locale } = useTranslation('wp');

  // Only ever offer finalise on an APPROVED run — the server rejects any
  // other status with BAD_REQUEST, and the button must never open a
  // guaranteed-to-fail action. `voided`/`paid`/`filed`/`draft`/`in_review`
  // all render the FinalReviewStep body without a finalise button.
  const can_finalise = run.status === 'approved';

  return (
    <>
      <Alert variant="info" title={t('pay_run.confirm_locks_title')}>
        {t('pay_run.confirm_locks_help')}
      </Alert>
      <div className="mt-3.5">
        <FieldRow label={t('pay_run.pay_period')}>
          {format_month_year(run.period, locale)}
        </FieldRow>
        <FieldRow label={t('pay_run.employee_scope')}>
          {format_number(run.employee_count, locale)}
        </FieldRow>
        <NumericFieldRow label={t('pay_run.net_payable')}>
          {format_money(run.net_payable, run.currency, locale)}
        </NumericFieldRow>
      </div>
      {finalise_available && can_finalise ? (
        <div className="mt-3.5">
          <Button
            variant="primary"
            size="sm"
            disabled={is_finalising}
            onClick={on_finalise}
          >
            {is_finalising
              ? t('pay_run.finalising')
              : t('pay_run.finalise_cta')}
          </Button>
          {finalise_error === 'step_up_required' ? (
            <div className="mt-2" role="alert">
              <p className="nv-help is-error mt-0">
                {t('pay_run.finalise_step_up_required')}
              </p>
              <div className="flex gap-2 mt-2">
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={() => {
                    restartSignIn();
                  }}
                >
                  {t('auth.step_up.sign_in_again_cta')}
                </Button>
              </div>
            </div>
          ) : finalise_error === 'entitlement_required' ? (
            <p className="nv-help is-error mt-2" role="alert">
              {t('pay_run.entitlement_required_description')}
            </p>
          ) : finalise_error ? (
            <p className="nv-help is-error mt-2" role="alert">
              {t('pay_run.finalise_error')}
            </p>
          ) : null}
        </div>
      ) : !finalise_available ? (
        <p className="nv-help mt-3.5">{t('pay_run.read_only_notice')}</p>
      ) : (
        // finalise_available && !can_finalise: writable grant but the run
        // isn't in `approved` state (draft/in_review/paid/voided/…). Never
        // leave the wizard silent — surface the same status label the
        // history table shows so the user understands why no CTA is
        // offered. Prevents the "empty final step" UX flagged by the
        // adversarial review pass.
        <p className="nv-help mt-3.5">
          {t('pay_run.finalise_status_locked', {
            status: t(PAY_RUN_STATUS_KEY[run.status]),
          })}
        </p>
      )}
    </>
  );
}

/**
 * Post-run actions (C4c, #21956): payslip download, salary disbursement
 * file, statutory payment recording/voiding, and the filing calendar. Each
 * action is a self-contained section in `post_run_sections.tsx` — this
 * component is only the composition root + the "no real backend wired"
 * fallback, so it stays well under the 150-line extraction threshold.
 *
 * `voidPayRun`/`deletePayRun` UI, payslip PDF-email delivery, and recurring
 * pay schedules remain documented gaps, not built here — see the epic issue
 * (#21956)'s "out of scope" list.
 */
export function PostRunStep({
  run,
  post_actions,
}: {
  run: PayRun;
  post_actions: PayRunPostActionsController | undefined;
}): React.JSX.Element {
  const { t } = useTranslation('wp');

  if (!post_actions) {
    return (
      <Alert variant="info" title={t('pay_run.post_run.no_backend_title')}>
        {t('pay_run.post_run.no_backend_description')}
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      {/* Read-only-grant notice (C9, #22331) -- rendered ONCE here, not
          per-section, since `StatutorySection` AND `FilingSection` both
          hide write actions for the SAME reason (`post_actions.is_read_only_grant`)
          and mount together; showing it twice would be a duplicated,
          redundant paragraph (adversarial-review finding). */}
      {post_actions.is_read_only_grant && (
        <Alert variant="info" title={t('pay_run.post_run.read_only_notice')} />
      )}
      {/* Keyed per run: each section owns per-run local state (selected
          employee, fetched statutory rows, …) via useState initializers that
          only run once on mount. Without a key, a run swap while the wizard
          stays mounted (e.g. `start_pay_run` creating a new draft that wins
          `usePayRuns`' current-run selection) would leave that state stale
          instead of remounting fresh for the new run. Each key is ALSO unique
          among these siblings: three siblings sharing the bare `run.id` broke
          React's reconciliation, which kept stale copies on re-render and
          showed "Generate payslips" / "Generate bank payment file" twice. */}
      <PayslipSection
        key={`${run.id}:payslip`}
        run={run}
        post_actions={post_actions}
      />
      <DisbursementSection
        key={`${run.id}:disbursement`}
        run={run}
        post_actions={post_actions}
      />
      <StatutorySection
        key={`${run.id}:statutory`}
        run={run}
        post_actions={post_actions}
      />
      <FilingSection post_actions={post_actions} />
    </div>
  );
}
